/**
 * InviteSummaryCard — top-of-page card on the /join/<token> route.
 *
 * Renders the workspace + inviter + role + masked phone the invite is
 * for. The status pill (pending / accepted / cancelled / expired)
 * drives whether the page shows the OTP form below.
 */
import { Building2, UserPlus } from "lucide-react";
import { Card, CardBody, StatusPill } from "@flint/ui";

export interface InviteSummaryCardProps {
  workspaceName: string;
  inviterName: string;
  /** Display label for the role — the granular role name (ADR-0030) when
   *  present, else the legacy band. */
  role: string;
  maskedPhone: string;
  status: "pending" | "accepted" | "cancelled" | "expired";
}

export function InviteSummaryCard({
  workspaceName,
  inviterName,
  role,
  maskedPhone,
  status,
}: InviteSummaryCardProps) {
  return (
    <Card>
      <CardBody className="p-5 sm:p-6 flex flex-col gap-4">
        <div className="flex items-start gap-4">
          <span
            aria-hidden
            className="inline-grid place-items-center h-12 w-12 rounded-md bg-sky-50 text-sky-700 shrink-0"
          >
            <Building2 size={22} strokeWidth={2} />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold text-ink leading-tight truncate">
              {workspaceName}
            </h1>
            <p className="mt-0.5 text-sm text-muted leading-relaxed">
              <UserPlus size={12} strokeWidth={2} aria-hidden className="inline -mt-0.5 mr-1" />
              <strong className="text-ink-2">{inviterName}</strong> invited you to join as{" "}
              <strong className="text-ink-2">{role}</strong>
            </p>
            <p className="mt-2 text-xs text-muted">
              For phone <span className="font-mono text-ink-2">{maskedPhone}</span>
            </p>
          </div>
          {status === "pending" ? (
            <StatusPill tone="sky" size="sm">
              Pending
            </StatusPill>
          ) : status === "accepted" ? (
            <StatusPill tone="ok" size="sm">
              Accepted
            </StatusPill>
          ) : status === "cancelled" ? (
            <StatusPill tone="bad" size="sm">
              Cancelled
            </StatusPill>
          ) : (
            <StatusPill tone="warn" size="sm">
              Expired
            </StatusPill>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
